/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { MINUTE_TO_PIXEL_FACTOR, PROGRAM_CELL_TRAILING_ICONS_WIDTH_OFFSET } from '../../../config';
import { DimensionScaler } from '../../../core/device/DimensionScaler';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { GuideType } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { RecordingRestriction } from '../../../core/usecases';
import { Mousetrap, RestrictionMessage } from '../../common';
import { FilteredGuideHighlight } from '../FilteredGuide/FilteredGuideHighlight/FilteredGuideHighlight';
import { GuideHighlight } from '../GuideHighlight';
import { RecordingStatusIcon } from '../RecordingStatusIcon';
import { CellTypes } from './CellTypes';
import { ProgramCellProps } from './ProgramCellProps';

import './ProgramCell.scss';

const ProgramCell: FunctionComponent<ProgramCellProps> = (props): JSX.Element => {
    const {
        channel,
        duration,
        endTime,
        highlightId,
        isCatchup,
        isLeft,
        isNew,
        isPastGuideMode,
        isRight,
        isStartover,
        isSubscribed,
        movieYear,
        programCell,
        startTime,
        title,
        type,
        cuAvailabilityDuration,
        isRefreshed,
        recordingStatus,
        guideType,
    } = props;

    const [isSocu, setIsSocu] = useState<boolean>(false);
    const [showNewIcon, setShowNewIcon] = useState<boolean>(!!isNew);
    const [decorativeIconSpanWidth, setDecorativeIconSpanWidth] = useState<number>(0);
    const setSoStatusTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
    const setCuStatusTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
    const socuIconSpanRef = useRef<HTMLImageElement>(null);
    const programCellContainerRef = useRef<HTMLDivElement>(null);
    const programCellRef = useRef<HTMLDivElement>(null);
    const programCellSpanTextRef = useRef<HTMLSpanElement>(null);
    const decorativeIconSpanRef = useRef<HTMLSpanElement>(null);

    const cellType = type || CellTypes.DEFAULT;
    const programCellTitle = title || '';
    const displayText = movieYear ? `${programCellTitle} (${movieYear})` : programCellTitle;
    const from = displayText.length - 4 < 0 ? 0 : displayText.length - 4;
    const trailingText = displayText.slice(from, from + 4) || '';
    let containerWidth = (duration || 0) * MINUTE_TO_PIXEL_FACTOR - 3;
    const isSafeLeft = containerWidth > 20 && isLeft;
    const isSafeRight = containerWidth > 20 && isRight;
    const customStyle: any = {};
    const services = new ServicesClientImpl();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const socuIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.SOURCE_SOCU_ICON);

    const isNonTrailingOrLeading = !(
        cellType === CellTypes.TRAILING || cellType === CellTypes.LEADING
    );

    if (cellType === CellTypes.FUTURE_CONTINUOUS && !isPastGuideMode) {
        containerWidth += 6;
        customStyle.marginRight = 0;
    }
    customStyle.width = DimensionScaler.getInstance().getScaledWidth(containerWidth, false);

    const programCellContainerWidth = programCellContainerRef.current?.clientWidth || 0;
    const programCellWidth = programCellRef.current?.clientWidth || 0;
    const hasOverflow =
        (programCellSpanTextRef.current?.scrollWidth || 0) >
        programCellWidth - decorativeIconSpanWidth;
    const programCellSpanTextClasses = `text${!isSubscribed ? ' cell-fadeout' : ''}${hasOverflow ? ' cell-fade-effect' : ''}`;

    // here 24 is margin from left and right side of the cell.
    let programCellSpanTextWidth = programCellWidth - 24 - decorativeIconSpanWidth;
    programCellSpanTextWidth =
        programCellSpanTextWidth > programCellContainerWidth
            ? programCellContainerWidth
            : programCellSpanTextWidth;
    const decorativeIconPosition =
        containerWidth - (programCellSpanTextWidth + decorativeIconSpanWidth) - 24;

    /* creating the classname from recordingStatus string by replacing uppercase
       letter with -matched letter in lowercase.
       e.g. scheduledAsOnePass -> scheduled-as-one-pass */
    const recordingStatusClass = recordingStatus?.replace(
        /[A-Z]/g,
        (match) => `-${match.toLowerCase()}`,
    );

    const disableCatchupAfterCatchupDuration = () => {
        if (cuAvailabilityDuration && startTime) {
            const timeToDisableCu = cuAvailabilityDuration - (Date.now() - startTime.getTime());
            setCuStatusTimeoutId.current = setTimeout(() => {
                setIsSocu(false);
            }, timeToDisableCu);
        }
    };

    const validateAndSetCatchupStatus = () => {
        if (
            cuAvailabilityDuration &&
            endTime &&
            Date.now() - endTime.getTime() <= cuAvailabilityDuration
        ) {
            setIsSocu(true);
            disableCatchupAfterCatchupDuration();
        } else {
            setIsSocu(false);
        }
    };

    const checkStartoverAndSetStatus = () => {
        if (isStartover && startTime && endTime) {
            const currentTime = Date.now();
            const isCurrentlyAiring =
                startTime.getTime() <= currentTime && currentTime < endTime.getTime();
            const willAirInFuture = currentTime < startTime.getTime();
            if (isCurrentlyAiring) setIsSocu(true);
            else if (willAirInFuture) {
                const timeToEnableStartover = startTime.getTime() - currentTime;
                setSoStatusTimeoutId.current = setTimeout(() => {
                    setIsSocu(true);
                }, timeToEnableStartover);
            }
        }
    };

    const checkCatchupAndSetStatus = () => {
        if (isCatchup && endTime && startTime) {
            const currentTime = Date.now();
            const isPastProgram = endTime.getTime() <= currentTime;
            const willAirInFuture = currentTime < startTime.getTime();
            if (isPastProgram) {
                validateAndSetCatchupStatus();
            } else if (willAirInFuture) {
                const timeToEnableCu = endTime.getTime() - currentTime;
                setCuStatusTimeoutId.current = setTimeout(() => {
                    validateAndSetCatchupStatus();
                }, timeToEnableCu);
            }
        }
    };

    const clearTimeouts = () => {
        if (setSoStatusTimeoutId.current) clearTimeout(setSoStatusTimeoutId.current);
        if (setCuStatusTimeoutId.current) clearTimeout(setCuStatusTimeoutId.current);
    };

    useEffect(() => {
        if (isRefreshed) {
            clearTimeouts();
            checkStartoverAndSetStatus();
            checkCatchupAndSetStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRefreshed]);

    useLayoutEffect(() => {
        if (decorativeIconSpanRef.current) {
            const newWidth = decorativeIconSpanRef.current.scrollWidth;
            if (newWidth !== decorativeIconSpanWidth) {
                setDecorativeIconSpanWidth(newWidth);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordingStatus, isSocu, showNewIcon]);

    useEffect(() => {
        if (isSocu)
            setShowNewIcon((showIcon) => {
                return (
                    showIcon &&
                    decorativeIconSpanWidth + PROGRAM_CELL_TRAILING_ICONS_WIDTH_OFFSET <
                        (programCellRef.current?.offsetWidth || 0)
                );
            });
        else {
            setShowNewIcon(!!isNew);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSocu, decorativeIconSpanWidth]);

    useEffect(() => {
        checkStartoverAndSetStatus();
        checkCatchupAndSetStatus();
        return () => {
            clearTimeouts();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            {isNonTrailingOrLeading ? (
                <Mousetrap
                    className="program-cell-container"
                    value={highlightId || ''}
                    style={customStyle}
                    ref={programCellContainerRef}
                >
                    {guideType === GuideType.FILTERED_GUIDE ? (
                        <FilteredGuideHighlight id={highlightId} cellType={cellType} />
                    ) : (
                        <GuideHighlight id={highlightId} cellType={cellType} />
                    )}

                    <RestrictionMessage
                        displayType={RecordingRestriction.PROGRAM_CELL}
                        channel={channel}
                        programCell={programCell}
                    />
                    <div ref={programCellRef} className="program-cell">
                        {isSafeLeft ? <span className="left-pointer"></span> : null}
                        <span
                            ref={programCellSpanTextRef}
                            className={programCellSpanTextClasses}
                            style={{
                                width: hasOverflow
                                    ? `${((programCellSpanTextWidth / (programCellContainerRef.current?.offsetWidth || 1)) * 100).toFixed(2)}%`
                                    : 'auto',
                            }}
                        >
                            {displayText}
                        </span>
                        <span
                            className="decorative-icons"
                            ref={decorativeIconSpanRef}
                            style={{
                                right: `${((decorativeIconPosition / (programCellContainerRef.current?.offsetWidth || 1)) * 100).toFixed(2)}`,
                            }}
                        >
                            {showNewIcon ? (
                                <img
                                    src={newIcon?.imageUrl}
                                    className="icon-new"
                                    data-testid={'program-cell-new-icon'}
                                />
                            ) : null}
                            {isSocu ? (
                                <img
                                    src={socuIcon?.imageUrl}
                                    className="icon-socu"
                                    ref={socuIconSpanRef}
                                />
                            ) : null}
                            {recordingStatus ? (
                                <RecordingStatusIcon
                                    type={recordingStatus}
                                    styleClass={recordingStatusClass}
                                />
                            ) : null}
                        </span>
                        {isSafeRight ? <span className="right-pointer"></span> : null}
                    </div>
                </Mousetrap>
            ) : null}

            {cellType === CellTypes.TRAILING ? (
                <div className="program-cell-trailing-container">
                    <div className="text">{trailingText}</div>
                </div>
            ) : null}

            {cellType === CellTypes.LEADING ? (
                <div className="program-cell-leading-container">
                    <div className="text">{displayText}</div>
                </div>
            ) : null}
        </React.Fragment>
    );
};

export default ProgramCell;
